import { Link, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { LogOut, Menu, Home, History, Wallet, User, Key, Bot, Handshake } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Sheet, SheetContent, SheetHeader, SheetTrigger } from "@/components/ui/sheet";
import useAppSettingsStore from "@/store/appSettingsStore";
import packageJson from "../../package.json";
import { useMobileMenu } from "@/context/MobileMenuContext";
import SocketStatus from "@/components/SocketStatus";
import { getConfig } from "@/utils/config";

// منوهای ثابت کاربر
const USER_MENUS = [
  { id: 'dashboard', label: 'خرید/فروش', path: '/', icon: Home },
  { id: 'lafz', label: 'لفظ', path: '/lafz', icon: Handshake },
  { id: 'history', label: 'صورت حساب', path: '/history', icon: History },
  // { id: 'wallet', label: 'کیف پول', path: '/wallet', icon: Wallet },
  { id: 'profile', label: 'پروفایل', path: '/profile', icon: User },
  // { id: 'change-password', label: 'تغییر رمز عبور', path: '/change-password', icon: Key },
];

export default function Sidebar() {
  const { logout, isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { settings, fetchSettings } = useAppSettingsStore();
  const { isOpen: isMobileMenuOpen, setIsOpen: setIsMobileMenuOpen } = useMobileMenu();
  const [userPhoneNumber, setUserPhoneNumber] = useState(null);
  const [lafzIsEnable, setLafzIsEnable] = useState(false);
  const marketMakerBotUrl = getConfig('VITE_MARKET_MAKER_BOT_URL', '');

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  // Get user phoneNumber from API response stored in localStorage
  useEffect(() => {
    const updateCachedUserFlags = () => {
      if (isAuthenticated) {
        try {
          const cached = localStorage.getItem('app_public_settings');
          if (cached) {
            const cachedData = JSON.parse(cached);
            if (cachedData?.phoneNumber) {
              setUserPhoneNumber(cachedData.phoneNumber);
            }
            setLafzIsEnable(cachedData?.lafzIsEnable === true);
          }
        } catch (err) {
          console.warn('Failed to read user flags from cache:', err);
        }
      } else {
        setLafzIsEnable(false);
      }
    };

    updateCachedUserFlags();

    // Listen for localStorage changes
    const handleStorageChange = (e) => {
      if (e.detail?.key === 'app_public_settings') {
        updateCachedUserFlags();
      }
    };

    window.addEventListener('localStorageChange', handleStorageChange);
    return () => {
      window.removeEventListener('localStorageChange', handleStorageChange);
    };
  }, [isAuthenticated]);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const isActive = (path) => location.pathname === path;

  const NavigationContent = () => {
    const getLinkClassName = (path) => {
      const baseClasses = "flex items-center gap-2 px-2 py-1.5 rounded-md transition-colors";
      const activeClasses = isActive(path)
        ? "bg-white/20 text-white font-medium"
        : "text-white/70 hover:text-white hover:bg-white/10";
      return `${baseClasses} ${activeClasses}`;
    };

    if (!isAuthenticated) {
      return (
        <nav className="space-y-2 text-sm">
          <Link className={getLinkClassName("/login")} to="/login">
            <User size={16} /> ورود
          </Link>
        </nav>
      );
    }

    return (
      <nav className="space-y-2 text-sm">
        {USER_MENUS.filter((menu) => menu.id !== "lafz" || lafzIsEnable).map((menu) => {
          const IconComponent = menu.icon;
          return (
            <Link key={menu.id} className={getLinkClassName(menu.path)} to={menu.path}>
              <IconComponent size={16} />
              <span className="flex-1">{menu.label}</span>
            </Link>
          );
        })}
        <button
          className="flex items-center gap-2 px-2 py-1.5 rounded-md transition-colors text-red-200 hover:text-red-100 hover:bg-white/10 mt-4 w-full"
          onClick={handleLogout}
        >
          <LogOut size={16} /> خروج
        </button>
      </nav>
    );
  };

  const FooterContent = () => {
    return (
      <div className="mt-auto">
        {isAuthenticated && marketMakerBotUrl && (
          <a
            href={marketMakerBotUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 px-2 py-1.5 mb-2 rounded-md transition-colors text-sm text-white/70 hover:text-white hover:bg-white/10"
          >
            <Bot size={16} />
            <span>ربات معامله‌گردان</span>
          </a>
        )}
        <div className="pt-4 border-t border-white/20">
          <div className="flex items-center justify-center gap-4 text-white">
            {settings.instagram_id && (
              <a href={`https://instagram.com/${settings.instagram_id.replace('@', '')}`} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="hover:opacity-90 transition-opacity">
                <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 448 512" height="20" width="20" xmlns="http://www.w3.org/2000/svg">
                  <path d="M224.1 141c-63.6 0-114.9 51.3-114.9 114.9s51.3 114.9 114.9 114.9S339 319.5 339 255.9 287.7 141 224.1 141zm0 189.6c-41.1 0-74.7-33.5-74.7-74.7s33.5-74.7 74.7-74.7 74.7 33.5 74.7 74.7-33.6 74.7-74.7 74.7zm146.4-194.3c0 14.9-12 26.8-26.8 26.8-14.9 0-26.8-12-26.8-26.8s12-26.8 26.8-26.8 26.8 12 26.8 26.8zm76.1 27.2c-1.7-35.9-9.9-67.7-36.2-93.9-26.2-26.2-58-34.4-93.9-36.2-37-2.1-147.9-2.1-184.9 0-35.8 1.7-67.6 9.9-93.9 36.1s-34.4 58-36.2 93.9c-2.1 37-2.1 147.9 0 184.9 1.7 35.9 9.9 67.7 36.2 93.9s58 34.4 93.9 36.2c37 2.1 147.9 2.1 184.9 0 35.9-1.7 67.7-9.9 93.9-36.2 26.2-26.2 34.4-58 36.2-93.9 2.1-37 2.1-147.8 0-184.8zM398.8 388c-7.8 19.6-22.9 34.7-42.6 42.6-29.5 11.7-99.5 9-132.1 9s-102.7 2.6-132.1-9c-19.6-7.8-34.7-22.9-42.6-42.6-11.7-29.5-9-99.5-9-132.1s-2.6-102.7 9-132.1c7.8-19.6 22.9-34.7 42.6-42.6 29.5-11.7 99.5-9 132.1-9s102.7-2.6 132.1 9c19.6 7.8 34.7 22.9 42.6 42.6 11.7 29.5 9 99.5 9 132.1s2.7 102.7-9 132.1z"></path>
                </svg>
              </a>
            )}
            {settings.whatsapp_number && (
              <a href={`https://wa.me/${settings.whatsapp_number.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" aria-label="WhatsApp" className="hover:opacity-90 transition-opacity">
                <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 448 512" height="20" width="20" xmlns="http://www.w3.org/2000/svg">
                  <path d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222 0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222 0-59.3-25.2-115-67.1-157zm-157 341.6c-33.2 0-65.7-8.9-94-25.7l-6.7-4-69.8 18.3L72 359.2l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2 0-101.7 82.8-184.5 184.6-184.5 49.3 0 95.6 19.2 130.4 54.1 34.8 34.9 56.2 81.2 56.1 130.5 0 101.8-84.9 184.6-186.6 184.6zm101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18-5.1-1.9-8.8-2.8-12.5 2.8-3.7 5.6-14.3 18-17.6 21.8-3.2 3.7-6.5 4.2-12 1.4-32.6-16.3-54-29.1-75.5-66-5.7-9.8 5.7-9.1 16.3-30.3 1.8-3.7.9-6.9-.5-9.7-1.4-2.8-12.5-30.1-17.1-41.2-4.5-10.8-9.1-9.3-12.5-9.5-3.2-.2-6.9-.2-10.6-.2-3.7 0-9.7 1.4-14.8 6.9-5.1 5.6-19.4 19-19.4 46.3 0 27.3 19.9 53.7 22.6 57.4 2.8 3.7 39.1 59.7 94.8 83.8 35.2 15.2 49 16.5 66.6 13.9 10.7-1.6 32.8-13.4 37.4-26.4 4.6-13 4.6-24.1 3.2-26.4-1.3-2.5-5-3.9-10.5-6.6z"></path>
                </svg>
              </a>
            )}
            {settings.telegram_number && (
              <a href={`https://t.me/${settings.telegram_number.replace('@', '').replace('https://t.me/', '')}`} target="_blank" rel="noopener noreferrer" aria-label="Telegram" className="hover:opacity-90 transition-opacity">
                <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 448 512" height="20" width="20" xmlns="http://www.w3.org/2000/svg">
                  <path d="M446.7 98.6l-67.6 318.8c-5.1 22.5-18.4 28.1-37.3 17.5l-103-75.9-49.7 47.8c-5.5 5.5-10.1 10.1-20.7 10.1l7.4-104.9 190.9-172.5c8.3-7.4-1.8-11.5-12.9-4.1L117.8 284 16.2 252.2c-22.1-6.9-22.5-22.1 4.6-32.7L418.2 66.4c18.4-6.9 34.5 4.1 28.5 32.2z"></path>
                </svg>
              </a>
            )}
          </div>
          {settings.address && (
            <div className="mt-3 text-center text-xs text-white/90">{settings.address}</div>
          )}
          <div className="mt-3 text-center space-y-0.5">
            <div className="text-xs text-white/60">نسخه {packageJson.version}</div>
            <div className="text-xs text-white/75 leading-tight">
              طراحی شده توسط{" "}
              <a href="https://bazartalairan.ir/" target="_blank" rel="noopener noreferrer" className="text-white/95 hover:underline">
                بازار طلای ایران
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Mobile header */}
      <header className="sticky top-0 z-10 flex items-center gap-3 p-4 border-b bg-primary text-primary-foreground md:hidden">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
            <SheetTrigger asChild>
              <button aria-label="menu" className="md:hidden text-primary-foreground shrink-0">
                <Menu size={22} />
              </button>
            </SheetTrigger>
            <SheetContent side="right" dir="rtl" className="sidebar-bg text-white flex flex-col p-0">
              <SheetHeader className="px-6 pt-6 pb-4 flex-shrink-0">
                {settings.app_logo && (
                  <img src={settings.app_logo} alt={settings.app_name || "brand"} className="w-full h-16 object-contain" />
                )}
              </SheetHeader>
              <div className="flex flex-col flex-1 min-h-0 px-6 pb-6">
                {user && (
                  <div className="mb-4 pb-4 border-b border-white/20 flex-shrink-0">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-white truncate">{user.fullName || user.name}</div>
                        {(userPhoneNumber || user.phone) && (
                          <div className="text-xs text-white/70 mt-1 truncate">{userPhoneNumber || user.phone}</div>
                        )}
                      </div>
                      <div className="flex items-center gap-2" title="متصل">
                        <SocketStatus />
                      </div>
                    </div>
                  </div>
                )}
                <div className="flex-1 overflow-y-auto min-h-0 scrollbar-hide">
                  <NavigationContent />
                </div>
                <div className="flex-shrink-0 mt-4">
                  <FooterContent />
                </div>
              </div>
            </SheetContent>
          </Sheet>
          <div className="flex-1 min-w-0">
            <div className="text-lg font-semibold truncate text-primary-foreground flex-row flex">
              <span>{settings.app_name || 'سامانه معاملات'}</span>
              <span className="flex items-center gap-2 mr-1 mb-2" title="متصل">
                <SocketStatus />
              </span>
            </div>
            {(user?.fullName || user?.name) && <div className="text-sm text-primary-foreground/80 truncate">{user.fullName || user.name}</div>}
          </div>
          
        </div>
        {settings.app_logo && (
          <img src={settings.app_logo} alt={settings.app_name || "brand"} className="h-12 w-auto object-contain shrink-0" />
        )}
        
        
      </header>

      {/* Desktop sidebar */}
      <aside className="hidden md:flex flex-col fixed inset-y-0 right-0 w-64 border-l sidebar-bg text-white z-20">
        <div className="flex flex-col h-full p-4">
          {settings.app_logo && (
            <div className="mb-4 flex-shrink-0 ">
              <img src={settings.app_logo} alt={settings.app_name || "brand"} className="w-full h-16 object-contain" />
            </div>
          )}
          {user && (
            <div className="mb-4 pb-4 border-b border-white/20 flex-shrink-0">
              <div className="flex items-center justify-center font-nastaliq mb-1" style={{fontSize:'1.8rem'}}>{settings.app_name}</div>
              <div className="flex items-center justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-white truncate">{user.fullName || user.name}</div>
                  {(userPhoneNumber || user.phone) && (
                    <div className="text-xs text-white/70 mt-1 truncate">{userPhoneNumber || user.phone}</div>
                  )}
                </div>
                <div className="flex items-center gap-2" title="متصل">
                  <SocketStatus />
                </div>
              </div>
            </div>
          )}
          <div className="flex-1 overflow-y-auto min-h-0 scrollbar-hide">
            <NavigationContent />
          </div>
          <div className="flex-shrink-0 mt-4">
            <FooterContent />
          </div>
        </div>
      </aside>
    </>
  );
}
